import { type Cell, type Table } from '@tanstack/react-table';
import { useVirtualizer } from '@tanstack/react-virtual';
import React, { useEffect } from 'react';
import { TableRow } from './TableRow';
import { loadErrorMessage, noResultsMessage, type TableEntity } from './table-entity';
import { hasActiveColumnFilter } from './table-filters';

interface TableShellProps<TData> {
  table: Table<TData>;
  onRowClick?: (row: TData) => void;
  header: (table: Table<TData>) => React.ReactNode;
  cell: (cellData: Cell<TData, unknown>, index: number) => React.ReactNode;
  footer?: React.ReactNode;
  rowClassName?: (row: TData) => string | undefined;
  renderExpandedRow?: (row: TData) => React.ReactNode | null;
  disableHover?: boolean;
  scrollElement: HTMLElement | null;
  isLoading?: boolean;
  entity: TableEntity;
  isFiltered?: boolean;
  isError?: boolean;
  emptyState?: React.ReactNode;
  onFetchMore?: () => void;
  isFetchingNextPage?: boolean;
}

const ESTIMATED_ROW_HEIGHT_PX = 36;
const ROW_OVERSCAN = 8;

export function TableShell<TData>(props: TableShellProps<TData>) {
  const {
    table,
    header,
    cell,
    onRowClick,
    footer,
    rowClassName,
    renderExpandedRow,
    disableHover,
    scrollElement,
    isLoading,
    entity,
    isFiltered,
    isError,
    emptyState,
    onFetchMore,
    isFetchingNextPage,
  } = props;

  const rows = table.getRowModel().rows;
  const headerCount = table.getHeaderGroups()[0]?.headers.length ?? 1;

  // Derive what we can: table state covers the search we were handed and the column filters,
  // whether the page controls them or useTable holds them internally. The `isFiltered` prop only
  // has to carry what never reaches us — the list pages that filter server-side and keep `search`
  // to themselves. A client-side filter is only to blame when there was something to hide: a list
  // emptied under a default filter has simply nothing left.
  const { globalFilter, columnFilters } = table.getState();
  const couldHideRows = table.options.manualFiltering || table.getCoreRowModel().rows.length > 0;
  const filtered =
    isFiltered || (couldHideRows && (!!globalFilter || hasActiveColumnFilter(columnFilters)));

  const virtualizer = useVirtualizer({
    count: rows.length,
    getScrollElement: () => scrollElement,
    estimateSize: () => ESTIMATED_ROW_HEIGHT_PX,
    overscan: ROW_OVERSCAN,
    // A table that is not laid out (display: none) measures every row at 0px: the virtualizer
    // would then fit a whole viewport of new rows on each commit and never settle (React #185).
    measureElement: (el) => el?.getBoundingClientRect().height || ESTIMATED_ROW_HEIGHT_PX,
  });

  const virtualItems = virtualizer.getVirtualItems();
  const totalSize = virtualizer.getTotalSize();
  const paddingTop = virtualItems.length > 0 ? virtualItems[0].start : 0;
  const paddingBottom =
    virtualItems.length > 0 ? totalSize - virtualItems[virtualItems.length - 1].end : 0;

  const lastVirtualIndex = virtualItems[virtualItems.length - 1]?.index;
  useEffect(() => {
    if (!onFetchMore || isFetchingNextPage || lastVirtualIndex === undefined) return;
    if (lastVirtualIndex >= rows.length - 1) {
      onFetchMore();
    }
  }, [lastVirtualIndex, rows.length, onFetchMore, isFetchingNextPage]);

  return (
    <table className="min-w-full table-fixed border-collapse">
      <colgroup>
        {table.getHeaderGroups()[0]?.headers.map((header) => (
          <col key={header.id} style={{ width: header.column.getSize() }} />
        ))}
      </colgroup>
      {header(table)}
      {isLoading ? (
        <tbody />
      ) : rows.length === 0 ? (
        <tbody>
          <tr>
            <td colSpan={headerCount} className="border-t border-gray-200 p-0">
              {emptyState ?? (
                <p className={`p-4 ${isError ? 'text-red-600' : 'text-gray-500'}`}>
                  {isError ? loadErrorMessage(entity) : noResultsMessage(entity, filtered)}
                </p>
              )}
            </td>
          </tr>
        </tbody>
      ) : (
        <>
          {paddingTop > 0 && (
            <tbody aria-hidden="true">
              <tr style={{ height: paddingTop }}>
                <td colSpan={headerCount} />
              </tr>
            </tbody>
          )}
          {virtualItems.map((virtualRow) => {
            const row = rows[virtualRow.index];
            const expandedContent = renderExpandedRow?.(row.original);
            return (
              <tbody key={row.id} data-index={virtualRow.index} ref={virtualizer.measureElement}>
                <TableRow
                  row={row}
                  onRowClick={onRowClick}
                  cell={cell}
                  rowClassName={rowClassName}
                  disableHover={disableHover}
                />
                {expandedContent && (
                  <tr>
                    <td colSpan={row.getVisibleCells().length}>{expandedContent}</td>
                  </tr>
                )}
              </tbody>
            );
          })}
          {paddingBottom > 0 && (
            <tbody aria-hidden="true">
              <tr style={{ height: paddingBottom }}>
                <td colSpan={headerCount} />
              </tr>
            </tbody>
          )}
        </>
      )}
      {footer}
    </table>
  );
}
